import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQuery } from "@apollo/client";
import { DownloadCloud, FileCheck, Loader2, RefreshCw, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DELETE_APPLICATION, DELETE_TEST_OOP, QUICK_OOP_STAGE_JUMP, QUICK_STAGE_JUMP, UPDATE_GLOBAL_DELAY } from '../graphql/mutations';
import { GET_ALL_APPLICATION_NUMBERS, GET_ALL_OOPS } from '../graphql/queries';
import COVActionDialog from './COVActionDialog';
import COVPresetModal from './COVPresetModal';
import CSAWActionDialog from './CSAWActionDialog';
import CSAWPresetModal from './CSAWPresetModal';
import PLTPActionDialog from './PLTPActionDialog';
import PLTPPresetModal from './PLTPPresetModal';
import PTPRActionDialog from './PTPRActionDialog';
import PTPRPresetModal from './PTPRPresetModal';

import BulkCreateApplications from './BulkCreateApplications';
import CreateInspectionModal from './CreateInspectionModal';
import PLTCPActionDialog from './PLTCPActionDialog';
import PLTCPPresetModal from './PLTCPPresetModal';
import TCEBPActionDialog from './TCEBPActionDialog';
import TCEBPPresetModal from './TCEBPPresetModal';

const TestingTab = ({
   handleCreateTestCSAW,
   handleCreateTestCOV,
   handleCreateTestPTPR,
   handleCreateTestPLTP,
   handleCreateTestPLTCP,
   handleCreateTestTCEBP,
   handleCreateTestOOP,
   refetchOOPs,
   exportFinancialDataJSON
}) => {
   const [selectedStage, setSelectedStage] = useState("");
   const [searchTerm, setSearchTerm] = useState("");
   const [showDeleteDialog, setShowDeleteDialog] = useState(false);
   const [applicationToDelete, setApplicationToDelete] = useState(null);
   const [currentDelay, setCurrentDelay] = useState(0);
   const [selectedOOP, setSelectedOOP] = useState("");
   const [selectedOOPStage, setSelectedOOPStage] = useState("");
   const [showCSAWActionDialog, setShowCSAWActionDialog] = useState(false);
   const [showCSAWPresetModal, setShowCSAWPresetModal] = useState(false);
   const [showCOVActionDialog, setShowCOVActionDialog] = useState(false);
   const [showCOVPresetModal, setShowCOVPresetModal] = useState(false);
   const [showPTPRActionDialog, setShowPTPRActionDialog] = useState(false);
   const [showPTPRPresetModal, setShowPTPRPresetModal] = useState(false);
   const [showPLTPActionDialog, setShowPLTPActionDialog] = useState(false);
   const [showPLTPPresetModal, setShowPLTPPresetModal] = useState(false);

   const [showPLTCPActionDialog, setShowPLTCPActionDialog] = useState(false);
   const [showPLTCPPresetModal, setShowPLTCPPresetModal] = useState(false);
   const [showTCEBPActionDialog, setShowTCEBPActionDialog] = useState(false);
   const [showTCEBPPresetModal, setShowTCEBPPresetModal] = useState(false);
   const [showInspectionModal, setShowInspectionModal] = useState(false);

   const [checkedApplications, setCheckedApplications] = useState({});

   const { data: applicationNumbersData, loading: applicationNumbersLoading, refetch } =
      useQuery(GET_ALL_APPLICATION_NUMBERS);

   const { data: oopsData, loading: oopsLoading } = useQuery(GET_ALL_OOPS);

   const [quickStageJump] = useMutation(QUICK_STAGE_JUMP, {
      onCompleted: () => {
         toast.success("Application stage updated successfully");
         setSelectedStage("");
      },
      onError: (error) => {
         toast.error(`Failed to update stage: ${error.message}`);
      }
   });

   const [deleteApplication] = useMutation(DELETE_APPLICATION, {
      onCompleted: (data) => {
         toast.success(data.deleteApplication.message);
         setSelectedStage("");
         // Refetch the applications list
         refetch();
      },
      onError: (error) => {
         toast.error(`Failed to delete application: ${error.message}`);
      }
   });

   const [updateGlobalDelay] = useMutation(UPDATE_GLOBAL_DELAY, {
      onCompleted: (data) => {
         toast.success(data.updateGlobalDelay.message);
         setCurrentDelay(data.updateGlobalDelay.currentDelay);
      },
      onError: (error) => {
         toast.error(`Failed to update delay: ${error.message}`);
      }
   });

   const [quickOOPStageJump] = useMutation(QUICK_OOP_STAGE_JUMP, {
      onCompleted: () => {
         toast.success("OOP stage updated successfully");
         setSelectedOOP("");
         setSelectedOOPStage("");
      },
      onError: (error) => {
         toast.error(`Failed to update OOP stage: ${error.message}`);
      }
   });

   const [deleteTestOOP] = useMutation(DELETE_TEST_OOP, {
      refetchQueries: [{ query: GET_ALL_OOPS }],
      onCompleted: (data) => {
         toast.success(data.deleteTestOOP.message);
      },
      onError: (error) => {
         toast.error(`Failed to delete OOP: ${error.message}`);
      }
   });

   // Filter applications based on search term
   const filteredApplications = applicationNumbersData?.getAllApplicationDetails?.filter(app =>
      app.applicationNumber.toLowerCase().includes(searchTerm.toLowerCase())
   ) || [];

   // Application stage configurations based on flow.md
   const stageConfigs = [
      {
         value: "initial",
         label: "Initial Submission",
         status: "Submitted",
         stage: "TechnicalStaffReview",
         flags: {
            acceptedByTechnicalStaff: false,
            approvedByTechnicalStaff: false,
            acceptedByReceivingClerk: false,
            recordedByReceivingClerk: false,
            acceptedByPENRCENROfficer: false,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "technicalStaff",
         label: "Technical Staff Review",
         status: "In Progress",
         stage: "TechnicalStaffReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: false,
            acceptedByReceivingClerk: false,
            recordedByReceivingClerk: false,
            acceptedByPENRCENROfficer: false,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: true,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "technicalStaffApproved",
         label: "Technical Staff Approved",
         status: "In Progress",
         stage: "ReceivingClerkReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: false,
            recordedByReceivingClerk: false,
            acceptedByPENRCENROfficer: false,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "receivingClerk",
         label: "Receiving Clerk Review",
         status: "In Progress",
         stage: "ReceivingClerkReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: false,
            acceptedByPENRCENROfficer: false,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "receivingClerkRecorded",
         label: "Receiving Clerk Recorded",
         status: "In Progress",
         stage: "PENRCENROfficerReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: false,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "penrCenrOfficer",
         label: "PENR/CENR Officer Review",
         status: "In Progress",
         stage: "PENRCENROfficerReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: false,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "penrCenrOfficerApproved",
         label: "PENR/CENR Officer Approved",
         status: "In Progress",
         stage: "ChiefReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: false,
            awaitingOOP: false,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "chiefReviewed",
         label: "Chief Reviewed",
         status: "In Progress",
         stage: "OOPCreation",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: true,
            OOPCreated: false,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "oopCreated",
         label: "OOP Created",
         status: "In Progress",
         stage: "InspectionReportCreation",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: false,
            isInspectionResultPassed: false,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "inspectionReportCreated",
         label: "Inspection Report Created",
         status: "In Progress",
         stage: "InspectionReportReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: false,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "inspectionReportChiefReviewed",
         label: "Inspection Report Chief Reviewed",
         status: "In Progress",
         stage: "InspectionReportReview",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: false,
            awaitingPermitCreation: false,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "inspectionReportPENRCENROfficerReviewed",
         label: "Inspection Report PENR/CENR Officer Reviewed",
         status: "In Progress",
         stage: "PermitCreation",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: true,
            PermitCreated: false,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "permitCreated",
         label: "Permit Created",
         status: "In Progress",
         stage: "CertificateSigning",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: false,
            PermitCreated: true,
            certificateSignedByPENRCENROfficer: false,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "certificateSignedByPENRCENROfficer",
         label: "Certificate Signed By PENR/CENR Officer",
         status: "In Progress",
         stage: "CertificateSigning",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: false,
            PermitCreated: true,
            certificateSignedByPENRCENROfficer: true,
            certificateSignedByForestRanger: false,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "certificateSignedByForestRanger",
         label: "Certificate Signed By Forest Ranger",
         status: "In Progress",
         stage: "CertificateSigning",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: false,
            PermitCreated: true,
            certificateSignedByPENRCENROfficer: true,
            certificateSignedByForestRanger: true,
            certificateSignedByChiefTSD: false,
            hasCertificate: false
         }
      },
      {
         value: "certificateSignedByChiefTSD",
         label: "Certificate Signed By Chief TSD",
         status: "In Progress",
         stage: "CertificateSigning",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: false,
            PermitCreated: true,
            certificateSignedByPENRCENROfficer: true,
            certificateSignedByForestRanger: true,
            certificateSignedByChiefTSD: true,
            hasCertificate: false
         }
      },
      {
         value: "certificateCreated",
         label: "Certificate Created",
         status: "Approved",
         stage: "Completed",
         flags: {
            acceptedByTechnicalStaff: true,
            approvedByTechnicalStaff: true,
            acceptedByReceivingClerk: true,
            recordedByReceivingClerk: true,
            acceptedByPENRCENROfficer: true,
            approvedByPENRCENROfficer: true,
            reviewedByChief: true,
            awaitingOOP: false,
            OOPCreated: true,
            hasInspectionReport: true,
            isInspectionResultPassed: true,
            InspectionReportsReviewedByChief: true,
            InspectionReportsReviewedByPENRCENROfficer: true,
            awaitingPermitCreation: false,
            PermitCreated: true,
            certificateSignedByPENRCENROfficer: true,
            certificateSignedByForestRanger: true,
            certificateSignedByChiefTSD: true,
            hasCertificate: true
         }
      }
   ];

   // OOP stage configurations
   const oopStageConfigs = [
      {
         value: "awaiting_manual",
         label: "Awaiting Manual Payment",
         description: "OOP is created and awaiting manual payment verification",
         oopStatus: "Awaiting Manual Payment",
         flags: {
            awaitingPaymentProofApproval: false,
            OOPSignedByTwoSignatories: true,
            OOPApproved: true,
            isManualPayment: true,
            isOnlinePayment: false
         }
      },
      {
         value: "completed",
         label: "Payment Completed",
         description: "Payment has been verified and completed",
         oopStatus: "Completed OOP",
         flags: {
            awaitingPaymentProofApproval: false,
            OOPSignedByTwoSignatories: true,
            OOPApproved: true,
            isManualPayment: true,
            isOnlinePayment: false,
            paymentProof: {
               status: "APPROVED",
               transactionId: `TRANS-${Date.now()}`,
               paymentMethod: "GCASH",
               amount: 500,
               referenceNumber: `REF-${Date.now()}`,
               payerDetails: {
                  name: "Test Payer",
                  email: "test@example.com",
                  phoneNumber: "09123456789"
               }
            }
         }
      },
      {
         value: "issued_or",
         label: "Issued Official Receipt",
         description: "Official Receipt has been issued",
         oopStatus: "Issued OR",
         flags: {
            awaitingPaymentProofApproval: false,
            OOPSignedByTwoSignatories: true,
            OOPApproved: true,
            isManualPayment: true,
            isOnlinePayment: false,
            paymentProof: {
               status: "APPROVED",
               transactionId: `TRANS-${Date.now()}`,
               paymentMethod: "GCASH",
               amount: 500,
               referenceNumber: `REF-${Date.now()}`,
               payerDetails: {
                  name: "Test Payer",
                  email: "test@example.com",
                  phoneNumber: "09123456789"
               }
            }
         }
      }
   ];

   const handleQuickJump = async () => {
      if (!selectedStage) {
         toast.error("Please select a stage");
         return;
      }

      const selectedConfig = stageConfigs.find(config => config.value === selectedStage);
      if (!selectedConfig) return;

      const selectedApplications = Object.keys(checkedApplications);

      if (selectedApplications.length === 0) {
         toast.error("Please select at least one application");
         return;
      }

      try {
         for (const applicationNumber of selectedApplications) {
            await quickStageJump({
               variables: {
                  input: {
                     applicationNumber,
                     stage: selectedConfig.stage,
                     status: selectedConfig.status,
                     flags: selectedConfig.flags
                  }
               }
            });
         }
         toast.success("Application stages updated successfully");
         setSelectedStage("");
      } catch (error) {
         console.error("Quick jump error:", error);
      }
   };

   const handleDeleteClick = async (appNumber) => {
      try {
         await deleteApplication({
            variables: {
               applicationNumber: appNumber
            }
         });
      } catch (error) {
         console.error("Delete error:", error);
      }
   };

   const handleDeleteConfirm = async () => {
      try {
         await deleteApplication({
            variables: {
               applicationNumber: applicationToDelete
            }
         });
      } catch (error) {
         console.error("Delete error:", error);
      } finally {
         setShowDeleteDialog(false);
         setApplicationToDelete(null);
      }
   };

   const handleDelayChange = async (value) => {
      try {
         await updateGlobalDelay({
            variables: {
               delay: value[0]
            }
         });
      } catch (error) {
         console.error("Error updating delay:", error);
      }
   };

   const handleOOPStageJump = async () => {
      if (!selectedOOP || !selectedOOPStage) {
         toast.error("Please select an OOP and stage");
         return;
      }

      const selectedConfig = oopStageConfigs.find(config => config.value === selectedOOPStage);
      if (!selectedConfig) return;

      try {
         await quickOOPStageJump({
            variables: {
               input: {
                  oopId: selectedOOP,
                  stage: selectedConfig.oopStatus,
                  flags: selectedConfig.flags
               }
            }
         });
      } catch (error) {
         console.error("OOP stage jump error:", error);
      }
   };

   const handleDeleteOOP = async (oopId) => {
      try {
         await deleteTestOOP({
            variables: { oopId }
         });
      } catch (error) {
         console.error('Error deleting OOP:', error);
      }
   };

   const handleCSAWButtonClick = () => {
      setShowCSAWActionDialog(true);
   };

   const handleCreateCSAWWithPreset = () => {
      setShowCSAWActionDialog(false);
      handleCreateTestCSAW();
   };

   const handleEditCSAWPreset = () => {
      setShowCSAWActionDialog(false);
      setShowCSAWPresetModal(true);
   };

   const handleSavePreset = (preset) => {
      localStorage.setItem('csawPreset', JSON.stringify(preset));
   };

   const handleCOVButtonClick = () => {
      setShowCOVActionDialog(true);
   };

   const handleCreateCOVWithPreset = () => {
      setShowCOVActionDialog(false);
      handleCreateTestCOV();
   };

   const handleEditCOVPreset = () => {
      setShowCOVActionDialog(false);
      setShowCOVPresetModal(true);
   };

   const handleSaveCOVPreset = (preset) => {
      localStorage.setItem('covPreset', JSON.stringify(preset));
   };

   const handlePTPRButtonClick = () => {
      setShowPTPRActionDialog(true);
   };

   const handleCreatePTPRWithPreset = () => {
      setShowPTPRActionDialog(false);
      handleCreateTestPTPR();
   };

   const handleEditPTPRPreset = () => {
      setShowPTPRActionDialog(false);
      setShowPTPRPresetModal(true);
   };

   const handleSavePTPRPreset = (preset) => {
      localStorage.setItem('ptprPreset', JSON.stringify(preset));
   };

   const handlePLTPButtonClick = () => {
      setShowPLTPActionDialog(true);
   };

   const handleCreatePLTPWithPreset = () => {
      setShowPLTPActionDialog(false);
      handleCreateTestPLTP();
   };

   const handleEditPLTPPreset = () => {
      setShowPLTPActionDialog(false);
      setShowPLTPPresetModal(true);
   };

   const handleSavePLTPPreset = (preset) => {
      localStorage.setItem('pltpPreset', JSON.stringify(preset));
   };

   const handlePLTCPButtonClick = () => {
      setShowPLTCPActionDialog(true);
   };

   const handleCreatePLTCPWithPreset = () => {
      setShowPLTCPActionDialog(false);
      handleCreateTestPLTCP();
   };

   const handleEditPLTCPPreset = () => {
      setShowPLTCPActionDialog(false);
      setShowPLTCPPresetModal(true);
   };

   const handleSavePLTCPPreset = (preset) => {
      localStorage.setItem('pltcpPreset', JSON.stringify(preset));
   };

   const handleTCEBPButtonClick = () => {
      setShowTCEBPActionDialog(true);
   };

   const handleCreateTCEBPWithPreset = () => {
      setShowTCEBPActionDialog(false);
      handleCreateTestTCEBP();
   };

   const handleEditTCEBPPreset = () => {
      setShowTCEBPActionDialog(false);
      setShowTCEBPPresetModal(true);
   };

   const handleSaveTCEBPPreset = (preset) => {
      localStorage.setItem('tcebpPreset', JSON.stringify(preset));
   };

   // Handle refresh function
   const handleRefresh = async () => {
      try {
         await refetch();
         toast.success("Applications list refreshed");
      } catch (error) {
         toast.error("Failed to refresh applications list");
      }
   };

   const handleRefreshOOPs = async () => {
      try {
         await refetchOOPs();
         toast.success('OOP list refreshed');
      } catch (error) {
         toast.error('Failed to refresh OOP list');
         console.error('Error refreshing OOPs:', error);
      }
   };

   const handleToggleApplication = (appNumber) => {
      setCheckedApplications((prevChecked) => {
         const newChecked = { ...prevChecked };
         if (newChecked[appNumber]) {
            delete newChecked[appNumber];
         } else {
            newChecked[appNumber] = true;
         }
         return newChecked;
      });
   };

   return (
      <div className="space-y-6">
         {/* Test Applications Section */}
         <Card>
            <CardHeader>
               <CardTitle>Create Test Applications</CardTitle>
               <CardDescription>Generate test applications for different permit types</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
               <Button onClick={handleCSAWButtonClick}>Create CSAW Application</Button>
               <Button onClick={handleCOVButtonClick}>Create COV Application</Button>
               <Button onClick={handlePTPRButtonClick}>Create PTPR Application</Button>
               <Button onClick={handlePLTPButtonClick}>Create PLTP Application</Button>
               {/* add this */}
               <Button onClick={handlePLTCPButtonClick}>Create PLTCP Application</Button>
               <Button onClick={handleTCEBPButtonClick}>Create TCEBP Application</Button>
            </CardContent>
         </Card>

         {/* Bulk Create Applications Section */}
         <BulkCreateApplications />

         {/* Quick Stage Jump Section */}
         <Card>
            <CardHeader>
               <div className="flex items-center justify-between">
                  <div>
                     <CardTitle>Application Management</CardTitle>
                     <CardDescription>Manage test applications and their stages</CardDescription>
                  </div>
                  <Button
                     variant="outline"
                     size="icon"
                     onClick={handleRefresh}
                     className="h-8 w-8"
                     title="Refresh applications list"
                  >
                     <RefreshCw className="h-4 w-4" />
                  </Button>
               </div>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="space-y-2">
                  <Label>Search Application Number</Label>
                  <div className="relative">
                     <Input
                        placeholder="Search application number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                     />
                     <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  <ScrollArea className="h-[200px] rounded-md border">
                     <div className="p-4 space-y-2">
                        {applicationNumbersLoading ? (
                           <div className="flex items-center justify-center py-4">
                              <span className="text-sm text-muted-foreground">Loading applications...</span>
                           </div>
                        ) : filteredApplications.length === 0 ? (
                           <p className="text-center text-sm text-muted-foreground py-4">
                              No applications found
                           </p>
                        ) : (
                           filteredApplications.map((app) => (
                              <div
                                 key={app.applicationNumber}
                                 className="flex items-center justify-between space-x-2 border-b pb-2 last:border-0"
                              >
                                 <div className="flex-1 space-y-1">
                                    <div className="flex items-center space-x-2">
                                       <input
                                          type="checkbox"
                                          checked={checkedApplications[app.applicationNumber] || false}
                                          onChange={() => handleToggleApplication(app.applicationNumber)}
                                          className="rounded border-gray-300 text-primary focus:ring-primary"
                                       />
                                       <div>
                                          <p className="text-sm font-medium leading-none">
                                             {app.applicationNumber}
                                          </p>
                                          <p className="text-sm text-muted-foreground">
                                             Status: {app.status} | Stage: {app.currentStage}
                                          </p>
                                       </div>
                                    </div>
                                 </div>
                                 <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleDeleteClick(app.applicationNumber)}
                                 >
                                    <Trash2 className="h-4 w-4" />
                                 </Button>
                              </div>
                           ))
                        )}
                     </div>
                  </ScrollArea>
               </div>

               <div className="space-y-2">
                  <Label>Target Stage</Label>
                  <Select value={selectedStage} onValueChange={setSelectedStage}>
                     <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                     </SelectTrigger>
                     <SelectContent>
                        {stageConfigs.map((config) => (
                           <SelectItem
                              key={config.value}
                              value={config.value}
                           >
                              {config.label}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
                  {selectedStage && (
                     <p className="text-sm text-muted-foreground">
                        {stageConfigs.find(config => config.value === selectedStage)?.description}
                     </p>
                  )}
               </div>

               <Button
                  onClick={handleQuickJump}
                  className="w-full"
                  variant="default"
                  disabled={!selectedStage}
               >
                  Jump to Stage
               </Button>
            </CardContent>
         </Card>

         {/* Inspection Management Section */}
         <Card>
            <CardHeader>
               <CardTitle>Inspection Management</CardTitle>
               <CardDescription>Create and manage test inspection schedules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="flex items-center gap-4">
                  <Button
                     onClick={() => setShowInspectionModal(true)}
                     className="flex items-center gap-2"
                  >
                     <FileCheck className="h-4 w-4" />
                     Create Test Inspection
                  </Button>
               </div>
            </CardContent>
         </Card>

         {/* Delay Simulation Section */}
         {/* <Card>
            <CardHeader>
               <CardTitle>Delay Simulation</CardTitle>
               <CardDescription>Add artificial delays to test loading states</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="space-y-2">
                  <div className="flex items-center justify-between">
                     <Label>Global API Delay</Label>
                     <span className="text-sm text-muted-foreground">{currentDelay}ms</span>
                  </div>
                  <Slider
                     value={[currentDelay]}
                     max={10000}
                     step={100}
                     onValueChange={handleDelayChange}
                     className="[&_[role=slider]]:h-4 [&_[role=slider]]:w-4"
                  />
                  <p className="text-sm text-muted-foreground">
                     Adds a delay of {currentDelay}ms to all API calls to simulate slow network conditions.
                  </p>
               </div>

               <div className="grid grid-cols-3 gap-2">
                  <Button
                     variant="outline"
                     onClick={() => handleDelayChange([0])}
                     className={currentDelay === 0 ? "border-primary" : ""}
                  >
                     No Delay
                  </Button>
                  <Button
                     variant="outline"
                     onClick={() => handleDelayChange([2000])}
                     className={currentDelay === 2000 ? "border-primary" : ""}
                  >
                     Slow (2s)
                  </Button>
                  <Button
                     variant="outline"
                     onClick={() => handleDelayChange([5000])}
                     className={currentDelay === 5000 ? "border-primary" : ""}
                  >
                     Very Slow (5s)
                  </Button>
               </div>
            </CardContent>
         </Card> */}

         {/* Create Test OOP Section */}
         <Card>
            <CardHeader>
               <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Create Test OOP</h3>
                  <Button
                     variant="outline"
                     size="sm"
                     onClick={handleRefreshOOPs}
                     className="h-8 px-2"
                  >
                     <RefreshCw className="h-4 w-4" />
                     <span className="ml-2">Refresh</span>
                  </Button>
               </div>
               <CardDescription>
                  Create a test Order of Payment with pre-signed signatures
               </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="space-y-2">
                  <Label>Application Number</Label>
                  <div className="flex gap-2">
                     <Input
                        placeholder="Search application number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1"
                     />
                  </div>
               </div>

               {/* Applications List */}
               <div className="space-y-2">
                  <Label>Select Application</Label>
                  <ScrollArea className="h-[200px] w-full rounded-md border">
                     <div className="p-4 space-y-2">
                        {applicationNumbersLoading ? (
                           <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
                           </div>
                        ) : filteredApplications.length === 0 ? (
                           <p className="text-sm text-muted-foreground text-center py-4">
                              No applications found
                           </p>
                        ) : (
                           filteredApplications.map((app) => (
                              <Button
                                 key={app.applicationNumber}
                                 variant="outline"
                                 className="w-full justify-between"
                                 onClick={() => handleCreateTestOOP(app.applicationNumber)}
                              >
                                 {app.applicationNumber}
                                 <FileCheck className="h-4 w-4 ml-2" />
                              </Button>
                           ))
                        )}
                     </div>
                  </ScrollArea>
               </div>
            </CardContent>
         </Card>

         {/* Financial Data Export Section */}
         <Card>
            <CardHeader>
               <CardTitle>Financial Data</CardTitle>
               <CardDescription>
                  Export sample financial data for MongoDB import
               </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="flex items-center gap-4">
                  <Button
                     onClick={exportFinancialDataJSON}
                     className="flex items-center gap-2"
                  >
                     <DownloadCloud className="h-4 w-4" />
                     Export Financial Data JSON
                  </Button>
               </div>
               <p className="text-sm text-muted-foreground">
                  This will generate a JSON file with sample official receipts data that you can import into MongoDB Compass.
                  After importing, toggle "Mock Financial Data" in the General tab to see the data in action.
               </p>
            </CardContent>
         </Card>

         {/* OOP Stage Jump Section */}
         <Card>
            <CardHeader>
               <CardTitle>OOP Stage Jump</CardTitle>
               <CardDescription>
                  Quickly jump OOP to different stages
               </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
               <div className="space-y-2">
                  <Label>Select OOP</Label>
                  <Select
                     value={selectedOOP}
                     onValueChange={setSelectedOOP}
                  >
                     <SelectTrigger>
                        <SelectValue placeholder="Select OOP" />
                     </SelectTrigger>
                     <SelectContent>
                        {oopsLoading ? (
                           <SelectItem value="loading" disabled>Loading OOPs...</SelectItem>
                        ) : oopsData?.getAllOOPs.map((oop) => (
                           <div key={oop.id} className="flex items-center gap-2">
                              <Button
                                 variant="outline"
                                 className="flex-1 justify-between"
                                 onClick={() => setSelectedOOP(oop.id)}
                              >
                                 {`${oop.billNo} (${oop.applicationNumber})`}
                              </Button>
                              <Button
                                 variant="destructive"
                                 size="icon"
                                 onClick={() => handleDeleteOOP(oop.id)}
                              >
                                 <Trash2 className="h-4 w-4" />
                              </Button>
                           </div>
                        ))}
                     </SelectContent>
                  </Select>
               </div>

               <div className="space-y-2">
                  <Label>Select Stage</Label>
                  <Select
                     value={selectedOOPStage}
                     onValueChange={setSelectedOOPStage}
                  >
                     <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                     </SelectTrigger>
                     <SelectContent>
                        {oopStageConfigs.map((config) => (
                           <SelectItem key={config.value} value={config.value}>
                              {config.label}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
                  {selectedOOPStage && (
                     <p className="text-sm text-muted-foreground">
                        {oopStageConfigs.find(c => c.value === selectedOOPStage)?.description}
                     </p>
                  )}
               </div>

               <Button
                  onClick={handleOOPStageJump}
                  className="w-full"
                  variant="default"
                  disabled={!selectedOOP || !selectedOOPStage}
               >
                  Jump to Stage
               </Button>
            </CardContent>
         </Card>

         {/* Delete Confirmation Dialog */}
         <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Delete Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to delete application {applicationToDelete}?
                     This action cannot be undone and will remove all related data.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleDeleteConfirm}
                     className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                     Delete
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <CSAWActionDialog
            isOpen={showCSAWActionDialog}
            onClose={() => setShowCSAWActionDialog(false)}
            onEditPreset={handleEditCSAWPreset}
            onCreateApplication={handleCreateCSAWWithPreset}
         />

         <CSAWPresetModal
            isOpen={showCSAWPresetModal}
            onClose={() => setShowCSAWPresetModal(false)}
            onSave={handleSavePreset}
            initialPreset={JSON.parse(localStorage.getItem('csawPreset') || 'null')}
         />

         <COVActionDialog
            isOpen={showCOVActionDialog}
            onClose={() => setShowCOVActionDialog(false)}
            onEditPreset={handleEditCOVPreset}
            onCreateApplication={handleCreateCOVWithPreset}
         />

         <COVPresetModal
            isOpen={showCOVPresetModal}
            onClose={() => setShowCOVPresetModal(false)}
            onSave={handleSaveCOVPreset}
            initialPreset={JSON.parse(localStorage.getItem('covPreset') || 'null')}
         />

         <PTPRActionDialog
            isOpen={showPTPRActionDialog}
            onClose={() => setShowPTPRActionDialog(false)}
            onEditPreset={handleEditPTPRPreset}
            onCreateApplication={handleCreatePTPRWithPreset}
         />

         <PTPRPresetModal
            isOpen={showPTPRPresetModal}
            onClose={() => setShowPTPRPresetModal(false)}
            onSave={handleSavePTPRPreset}
            initialPreset={JSON.parse(localStorage.getItem('ptprPreset') || 'null')}
         />

         <PLTPActionDialog
            isOpen={showPLTPActionDialog}
            onClose={() => setShowPLTPActionDialog(false)}
            onEditPreset={handleEditPLTPPreset}
            onCreateApplication={handleCreatePLTPWithPreset}
         />

         <PLTPPresetModal
            isOpen={showPLTPPresetModal}
            onClose={() => setShowPLTPPresetModal(false)}
            onSave={handleSavePLTPPreset}
            initialPreset={JSON.parse(localStorage.getItem('pltpPreset') || 'null')}
         />

         {/* add this  */}
         <PLTCPActionDialog
            isOpen={showPLTCPActionDialog}
            onClose={() => setShowPLTCPActionDialog(false)}
            onEditPreset={handleEditPLTCPPreset}
            onCreateApplication={handleCreatePLTCPWithPreset}
         />

         <PLTCPPresetModal
            isOpen={showPLTCPPresetModal}
            onClose={() => setShowPLTCPPresetModal(false)}
            onSave={handleSavePLTCPPreset}
            initialPreset={JSON.parse(localStorage.getItem('pltcpPreset') || 'null')}
         />

         <TCEBPActionDialog
            isOpen={showTCEBPActionDialog}
            onClose={() => setShowTCEBPActionDialog(false)}
            onEditPreset={handleEditTCEBPPreset}
            onCreateApplication={handleCreateTCEBPWithPreset}
         />

         <TCEBPPresetModal
            isOpen={showTCEBPPresetModal}
            onClose={() => setShowTCEBPPresetModal(false)}
            onSave={handleSaveTCEBPPreset}
            initialPreset={JSON.parse(localStorage.getItem('tcebpPreset') || 'null')}
         />

         <CreateInspectionModal
            isOpen={showInspectionModal}
            onClose={() => setShowInspectionModal(false)}
         />
      </div>
   );
};

export default TestingTab;